/**
 * <nh-carousel-controls>
 *
 * Previous and next buttons that live in a section header and drive the
 * resource-list carousel of the same section, plus an optional progress
 * hairline under the row. Used by the product-list section when
 * "Arrows beside View all" is on (homepage Signatures, 2026-10-20).
 *
 * The carousel's own on-image arrows stay in the DOM (hidden by CSS), so
 * the slideshow component keeps working exactly as before; this element
 * only calls its public next() / previous() and reads its scroll position.
 *
 * Markup:
 *   <nh-carousel-controls>
 *     <button data-nh-prev>...</button>
 *     <button data-nh-next>...</button>
 *   </nh-carousel-controls>
 *   ...
 *   <div class="nh-carousel-progress"><span data-nh-progress></span></div>
 */
class NhCarouselControls extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.section-resource-list');
    if (!this.section) return;

    this.prevButton = this.querySelector('[data-nh-prev]');
    this.nextButton = this.querySelector('[data-nh-next]');
    this.bar = this.section.querySelector('[data-nh-progress]');

    this.prevButton?.addEventListener('click', this.#onPrev);
    this.nextButton?.addEventListener('click', this.#onNext);

    // The slideshow upgrades asynchronously; wait for it before binding.
    customElements.whenDefined('slideshow-component').then(() => this.#bind());
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.#onPrev);
    this.nextButton?.removeEventListener('click', this.#onNext);
    this.scroller?.removeEventListener('scroll', this.#update);
    this.resizeObserver?.disconnect();
  }

  #bind() {
    this.slideshow = this.section.querySelector('slideshow-component.resource-list__carousel');
    this.scroller = this.slideshow?.querySelector(':scope > slideshow-container > slideshow-slides');
    if (!this.slideshow || !this.scroller) {
      this.hidden = true;
      return;
    }

    this.scroller.addEventListener('scroll', this.#update, { passive: true });
    this.resizeObserver = new ResizeObserver(this.#update);
    this.resizeObserver.observe(this.scroller);
    this.#update();
  }

  #onPrev = (event) => {
    this.slideshow?.previous(event);
  };

  #onNext = (event) => {
    this.slideshow?.next(event);
  };

  #update = () => {
    const { scroller } = this;
    if (!scroller) return;

    const max = scroller.scrollWidth - scroller.clientWidth;
    const pos = Math.abs(scroller.scrollLeft);
    const atStart = pos <= 1;
    const atEnd = max <= 1 || pos >= max - 1;

    // Nothing to scroll: hide the controls and the bar entirely.
    const scrollable = max > 1;
    this.toggleAttribute('data-static', !scrollable);
    if (this.bar) this.bar.parentElement.hidden = !scrollable;

    if (this.prevButton) this.prevButton.disabled = atStart;
    if (this.nextButton) this.nextButton.disabled = atEnd;

    // A disabled button drops keyboard focus to <body>; hand it to the
    // other arrow instead (WCAG 2.4.3).
    const focused = document.activeElement;
    if (focused === this.prevButton && atStart && !atEnd) this.nextButton?.focus();
    if (focused === this.nextButton && atEnd && !atStart) this.prevButton?.focus();

    if (this.bar && scrollable) {
      // Width of the bar = share of the row currently in view, offset by
      // how far along it is. Starts at e.g. 66% for four of six cards.
      const visible = scroller.clientWidth / scroller.scrollWidth;
      const offset = (pos / scroller.scrollWidth) * 100;
      this.bar.style.width = `${(visible * 100).toFixed(2)}%`;
      this.bar.style.insetInlineStart = `${offset.toFixed(2)}%`;
    }
  };
}

if (!customElements.get('nh-carousel-controls')) {
  customElements.define('nh-carousel-controls', NhCarouselControls);
}
